import {
  ColumnDef, ColumnPinningState, createColumnHelper, getCoreRowModel, getPaginationRowModel, getSortedRowModel, SortingState, useReactTable
} from '@tanstack/react-table';
import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import { AiFillEye } from 'react-icons/ai';
import { FiEdit2, FiRefreshCw } from 'react-icons/fi';
import { PiCaretCircleDownBold, PiCaretCircleUpBold, PiListMagnifyingGlass } from 'react-icons/pi';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast,ToastContainer } from 'react-toastify';
import { match } from 'ts-pattern';
import 'react-toastify/dist/ReactToastify.css';

import APIGateway from '@/api';
import ModalExampleShow from '@/app/evaluations/_modalExampleShow/modalExampleShow';
import ModalMetricShow from '@/app/evaluations/_modalMetricShow/modalMetricShow';
import ModalRunRerun from '@/app/evaluations/_modalRunRerun/modalRunRerun';
import ModalSourceShow from '@/app/evaluations/_modalSourceShow/modalSourceShow';
import { Button } from '@/components/button/button';
import { Icon } from '@/components/icon/icon';
import { Loader } from '@/components/loader/loader';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { StatusIcon } from '@/components/statusIcon/statusIcon';
import { Tooltip } from '@/components/tooltip/tooltip';
import { Heading } from '@/components/typography/heading';
import { useEvaluationRunResults, useSchedulesList } from '@/hooks/queries/useEvaluations';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';
import { useRunProgress } from '@/hooks/useRunProgress';
import {
  DetailMetricInfo, DetailMetricsInfo, Evaluation, MetricConfig, MetricDataMap, RunWithDetails, SOURCE_TYPES, TERMINAL_RUN_STATUSES
} from '@/types/evals';
import { formatCronCadence } from '@/utils/cronFormat';
import { formatScore } from '@/utils/numberUtils';
import { formatRunCreateError, parseRunCreateError } from '@/utils/runCreateError';
import { parseParams } from '@/utils/searchParams';
import { truncate } from '@/utils/stringUtils';
import { OutputFormatEnum, TimeFormatter, TimeSourceTypeEnum } from '@/utils/timeFormatter';

import styles from './page.module.scss';


const columnHelper = createColumnHelper<Record<string, unknown>>();

type SearchParams = {
  eid: string;
  rid: string;
};


function StatSummary( { metric }: { metric: DetailMetricInfo } ) {
  return (
    <div className={ clsx( styles.chart ) }>
      <div className={ styles.name }>{ metric.metricName }</div>
      <div className={ styles.average }>
        { formatScore( metric.averageScore ) }
      </div>
    </div>
  );
}

function Stats( { sMetricKeys, sMetricDataMap }: { sMetricKeys: string[], sMetricDataMap: MetricDataMap } ) {
  return sMetricKeys.map( ( metricKey, i ) => {
    const metric = sMetricDataMap[ metricKey ];
    return <StatSummary metric={ metric } key={ i } />;
  } );
}

function EvaluationInfoHeader( { evaluation, run, isRerunning, onRerunClick }: {
  evaluation?: Evaluation;
  run?: RunWithDetails<DetailMetricsInfo>;
  isRerunning: boolean;
  onRerunClick: () => void;
} ) {
  return (
    <>
      <span className={ clsx( styles.info_name ) }>
        <p>{ evaluation?.name || '' }</p>
      </span>
      <span className={ clsx( styles.info_page ) }>
        <p>Runs: { run?.run.id || '' }</p>
      </span>
      {
        run?.run.sourceId && (
          <Button
            variant='primary'
            icon={ <Icon icon={ FiRefreshCw } aria-hidden="true" /> }
            onClick={ onRerunClick }
            loading={ isRerunning }
            disabled={ isRerunning }
            title="Create a new run with the same source"
            aria-label="Create a new run with the same source"
            style={ { marginLeft: 'auto' } }
          >
            Rerun with Source
          </Button>
        )
      }
    </>
  );
}

function RunInfo( { run, sourceName, evaluation, metricProviderNames, metricProviderVersions, hasLitellmError, onShowSourceModal, onShowMetricModal }: {
  run?: RunWithDetails<DetailMetricsInfo>;
  sourceName?: string;
  evaluation?: Evaluation;
  metricProviderNames: Record<string, string>;
  metricProviderVersions: Record<string, Record<string, number>>;
  hasLitellmError: boolean;
  onShowSourceModal: () => void;
  onShowMetricModal: ( config: MetricConfig ) => void;
} ) {
  return (
    <>
      <span className={ clsx( styles.status ) }>
        <span>Run Status:</span>
        {
          run?.run.statusReason ? (
            <button
              className={ styles.preview_button }
              type="button"
              aria-label={ `Run status: ${ run.run.status }` }
            >
              <StatusIcon status={ run?.run.status } />
              <span className={ styles.tooltip_status_reason }>{run.run.statusReason}</span>
            </button>
          ) : (
            <StatusIcon status={ run?.run.status } />
          )
        }
        {
          hasLitellmError && (
            <span className={ styles.litellm_error }>This evaluation failed because a model it references does not exist. Update the evaluation metric to use a different model.</span>
          )
        }
      </span>
      {
        run && (
          <span className={ clsx( styles.status_item ) }>
            <span>Source:</span>
            {
              run.run.sourceId && sourceName ? (
                <span className={ styles.source_link }>
                  <button
                    className={ styles.preview_button }
                    onClick={ onShowSourceModal }
                    type="button"
                    aria-label={ `Preview source: ${ sourceName }` }
                  >
                    <span className={ styles.source_name }>{ `${ sourceName } (v${ run?.run.sourceVersion ?? 'Latest' })` }</span>
                    <PiListMagnifyingGlass aria-hidden="true" />
                    <span className={ styles.tooltip }>Preview source</span>
                  </button>
                  {
                    run?.run.sourceId && (
                      <Link
                        to={ `/evaluations/source?sid=${ run.run.sourceId }&version=${ run.run.sourceVersion ?? 'latest' }` }
                        aria-label="Open source page"
                      >
                        <FiEdit2 aria-hidden="true" />
                        <span className={ styles.tooltip }>Open source page</span>
                      </Link>
                    )
                  }
                </span>
              ) : (
                <span className={ styles.source_name }>Inline JSON</span>
              )
            }
          </span>
        )
      }
      {
        evaluation && (
          <span className={ clsx( styles.status_item ) }>
            <span>Metric Providers:</span>
            <span className={ styles.metrics_list }>
              {
                evaluation.metricProviders.map( ( metricProvider, i ) => {
                  const metricProviderId = metricProvider.metricProviderId;
                  const displayName = metricProviderNames[ metricProviderId ] || metricProviderId;
                  const versionId = metricProvider.versionId;
                  const versionNumber = versionId && metricProviderVersions[ metricProviderId ]?.[ versionId ];

                  return (
                    <React.Fragment key={ i }>
                      { i > 0 && <span>,</span> }
                      {
                        versionNumber ? (
                          <span className={ styles.metric_link }>
                            <button
                              className={ styles.preview_button }
                              onClick={ async() => {
                                try {
                                  const res = await APIGateway.evals.metricConfigGet( metricProviderId, versionNumber );
                                  onShowMetricModal( res );
                                } catch( e ) {
                                  console.error( e );
                                  toast.error( 'Unable to load metric configuration' );
                                }
                              } }
                              type="button"
                              aria-label={ `Preview metric: ${ displayName }` }
                            >
                              <span className={ styles.metric_name }>{ displayName }</span>
                              <PiListMagnifyingGlass aria-hidden="true" />
                              <span className={ styles.tooltip }>Preview metric</span>
                            </button>
                            <Link
                              to={ `/evaluations/metric?metricProviderId=${ encodeURIComponent( metricProviderId ) }&version=${ versionNumber }` }
                              aria-label="Open metric page"
                            >
                              <FiEdit2 aria-hidden="true" />
                              <span className={ styles.tooltip }>Open metric page</span>
                            </Link>
                          </span>
                        ) : (
                          <span className={ styles.metric_text } title={ displayName }>
                            { displayName }
                          </span>
                        )
                      }
                    </React.Fragment>
                  );
                } )
              }
            </span>
          </span>
        )
      }
    </>
  );
}

export default function EvaluationResultsPage() {
  const [ searchParams ] = useSearchParams();
  const {
    eid: qEvaluationId = '',
    rid: qRunId = ''
  } = parseParams<SearchParams>( searchParams );
  const navigate = useNavigate();

  const { data, isLoading: sIsLoading, error, refetch: refetchResults } = useEvaluationRunResults( qEvaluationId, qRunId );
  const [ useLocalZone ] = useLocalZonePreference();
  const scheduleId = data?.run?.run.scheduleId;
  const { data: schedules } = useSchedulesList(
    { evaluation_id: qEvaluationId, include_details: false, limit: 500 },
    { enabled: !!scheduleId }
  );
  const runSchedule = useMemo( () => schedules?.items?.find( s => s.schedule.id === scheduleId )?.schedule, [ schedules, scheduleId ] );

  // Stream progress for in-progress runs
  const runStatus = data?.run?.run.status;
  const isRunActive = !!runStatus && !( TERMINAL_RUN_STATUSES as readonly string[] ).includes( runStatus );
  const { progress: runProgress, isTerminal: runIsTerminal } = useRunProgress( qRunId || undefined, isRunActive );

  useEffect( () => {
    if( runIsTerminal ) {
      refetchResults();
    }
  }, [ runIsTerminal, refetchResults ] );

  const [ sShowSourceModal, setSShowSourceModal ] = useState<boolean>( false );
  const [ sShowMetricModal, setSShowMetricModal ] = useState<boolean>( false );
  const [ sSelectedMetricConfig, setSSelectedMetricConfig ] = useState<MetricConfig | undefined>();
  const [ sShowRerunModal, setSShowRerunModal ] = useState<boolean>( false );
  const [ sIsRerunning, setSIsRerunning ] = useState<boolean>( false );
  const [ sorting, setSorting ] = useState<SortingState>( [] );
  const [ columnPinning ] = useState<ColumnPinningState>( { left: [ 'conversationId' ] } );
  const [ sExpandedRows, setSExpandedRows ] = useState<Set<string>>( new Set() );
  const [ sSelectedExampleId, setSSelectedExampleId ] = useState<string | null>( null );
  const [ sShowExampleModal, setSShowExampleModal ] = useState<boolean>( false );

  // Extract data from query result
  const sEvaluation = data?.evaluation;
  const sRun = data?.run;
  const sMetricDataMap = data?.metricDataMap ?? {};
  const sMetricKeys = data?.metricKeys ?? [];
  const sExampleMetricDataMap = data?.exampleMetricDataMap ?? {};
  const sSource = data?.source;
  const sSourceName = data?.sourceName;
  const sMetricProviderNames = data?.metricProviderNames ?? {};
  const sMetricProviderVersions = data?.metricProviderVersions ?? {};

  // Show error toast if query fails
  useEffect( () => {
    if ( error ) {
      toast.error( 'Unable to get evaluations' );
    }
  }, [ error ] );

  /* HELPERS */
  const _handleToggleRow = ( exampleId: string ) => {
    setSExpandedRows( prev => {
      const next = new Set( prev );
      if( next.has( exampleId ) ) { next.delete( exampleId ); } else { next.add( exampleId ); }
      return next;
    } );
  };

  const _handleShowAllReasons = () => setSExpandedRows( new Set( sExampleIdsWithReasons ) );

  const _handleHideAllReasons = () => setSExpandedRows( new Set() );

  const sExampleIdsWithReasons = useMemo( () => Object.entries( sExampleMetricDataMap )
    .filter( ( [ , m ] ) => sMetricKeys.some( k => m[ k ]?.resultStatusReason || m[ k ]?.scoreMetaData?.reason ) )
    .map( ( [ id ] ) => id ),
  [ sExampleMetricDataMap, sMetricKeys ] );

  const sHasAnyReasons = sExampleIdsWithReasons.length > 0;
  const sAllReasonsShown = sHasAnyReasons && sExampleIdsWithReasons.every( id => sExpandedRows.has( id ) );

  // Memoize table columns
  const tableColumns = useMemo<ColumnDef<Record<string, unknown>>[]>( () => {
    return [
      columnHelper.accessor( 'conversationId', {
        id: 'conversationId',
        header: 'Conversation ID',
        cell: info => { const val = info.getValue() as string; return <Tooltip content={ val } className={ styles.truncate }>{ truncate( info.getValue() as string, 50 ) }</Tooltip>; },
        maxSize: 200,
        enableSorting: true,
        enablePinning: true
      } ),
      columnHelper.display( {
        id: 'reasons',
        header: 'Reasons',
        maxSize: 40,
        cell: info => {
          const exampleId = info.row.original[ 'conversationId' ] as string;
          const metricMap = sExampleMetricDataMap[ exampleId ] ?? {};
          const hasAnyReason = sMetricKeys.some(
            k => metricMap[ k ]?.resultStatusReason || metricMap[ k ]?.scoreMetaData?.reason
          );
          if( !hasAnyReason ) return null;
          const isExpanded = sExpandedRows.has( exampleId );
          const CaretIcon = isExpanded ? PiCaretCircleUpBold : PiCaretCircleDownBold;
          return (
            <button
              type="button"
              className={ styles.icon_button }
              aria-label={ isExpanded ? 'Collapse reasons' : 'Expand reasons' }
              onClick={ () => _handleToggleRow( exampleId ) }
            >
              <CaretIcon aria-hidden="true" />
            </button>
          );
        }
      } ),
      columnHelper.display( {
        id: 'runExample',
        header: 'Run Example',
        maxSize: 50,
        cell: info => {
          const exampleId = info.row.original[ 'conversationId' ] as string;
          return (
            <button
              type="button"
              className={ styles.icon_button }
              aria-label="View run example"
              onClick={ () => {
                setSSelectedExampleId( exampleId );
                setSShowExampleModal( true );
              } }
            >
              <AiFillEye aria-hidden="true" />
            </button>
          );
        }
      } ),
      ...sMetricKeys.map( metricKey => {
        const [ _, metricName ] = metricKey.split( '|' );

        return columnHelper.accessor( metricKey, {
          id: metricKey,
          header: metricName,
          cell: info => {
            const value = info.getValue();
            return ( value as { display?: unknown } )?.display ?? value;
          },
          sortingFn: ( rowA, rowB, columnId ) => {
            const a = rowA.getValue( columnId ) as { sortValue?: number; display?: unknown } | number | string;
            const b = rowB.getValue( columnId ) as { sortValue?: number; display?: unknown } | number | string;

            const aVal = typeof a === 'object' && a !== null ? ( a.sortValue ?? -Infinity ) : ( typeof a === 'number' ? a : parseFloat( String( a ) ) || -Infinity );
            const bVal = typeof b === 'object' && b !== null ? ( b.sortValue ?? -Infinity ) : ( typeof b === 'number' ? b : parseFloat( String( b ) ) || -Infinity );

            return aVal - bVal;
          },
          maxSize: 150,
          enableSorting: true
        } );
      } )
    ];
  }, [ sMetricKeys, sExpandedRows, sExampleMetricDataMap ] );

  // Memoize table data
  const tableData = useMemo<Record<string, unknown>[]>( () => {
    const data: Record<string, unknown>[] = [];

    // sort examples alphabetically for consistency across renders for object keys
    const sortedExampleIdData = Object.entries( sExampleMetricDataMap )
      .sort( ( [ exampleIdA ], [ exampleIdB ] ) => exampleIdA.localeCompare( exampleIdB ) );

    // iterate over ordered examples
    for( const [ exampleId, result ] of sortedExampleIdData ) {
      const row: Record<string, unknown> = { conversationId: exampleId };
      const isRowExpanded = sExpandedRows.has( exampleId );

      // use ordered found metric keys
      for( const metricKey of sMetricKeys ) {
        const resultData = result[ metricKey ];
        const score: string = resultData?.score || '-';

        let displayScore: string;
        let numericScore: number = -Infinity;
        try { // try parsing score as a number
          const parsedScore: number = parseFloat( score );

          if( isNaN( parsedScore ) ) throw Error();

          numericScore = parsedScore;
          displayScore = parsedScore.toFixed( 3 );
        } catch( e ) { // score isn't a number display as is
          displayScore = score;
        }

        const reason = resultData?.resultStatusReason || resultData?.scoreMetaData?.reason;

        if( reason ) {
          row[ metricKey ] = {
            sortValue: numericScore,
            display: (
              <div className={ styles.metric_cell }>
                <span className={ clsx( styles.result_score, styles.result_score_value ) }>{ displayScore }</span>
                { isRowExpanded && (
                  <span className={ styles.inline_reason }>{ reason }</span>
                ) }
              </div>
            )
          };
        } else {
          row[ metricKey ] = {
            sortValue: numericScore,
            display: <span className={ styles.result_score_value }>{ displayScore }</span>
          };
        }
      }

      data.push( row );
    }

    return data;
  }, [ sExampleMetricDataMap, sMetricKeys, sExpandedRows ] );

  const hasLitellmError = useMemo( () => {
    // errors from both router and proxy layers of litellm, to distinguish between
    // model errors and other errors that fall under litellm.BadRequestError
    const modelErrorPatterns = [
      'no healthy deployments',
      'invalid model name passed in',
      'no \'model_name\' with this string',
      'llm provider not provided'
    ];
    return Object.values( sExampleMetricDataMap ).some( result => sMetricKeys.some( metricKey => {
      const reason = result[ metricKey ]?.resultStatusReason?.toLowerCase();
      return reason?.includes( 'litellm.badrequesterror' ) &&
          modelErrorPatterns.some( pattern => reason.includes( pattern ) );
    } )
    );
  }, [ sExampleMetricDataMap, sMetricKeys ] );

  // Create table instance with column pinning, sorting, and pagination
  const resultsTable = useReactTable( {
    data: tableData,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableColumnPinning: true,
    state: {
      sorting,
      columnPinning
    },
    onSortingChange: setSorting,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 20
      }
    }
  } );

  const _handleRerun = async( includeReason: boolean ): Promise<void> => {
    if( !sRun?.run.sourceId ) {
      toast.error( 'No source ID available for rerun' );
      return;
    }

    try {
      setSIsRerunning( true );
      await APIGateway.evals.runCreate( qEvaluationId, {
        dataSource: {
          dataOrigin: 'reference',
          sourceId: sRun.run.sourceId,
          sourceVersion: sRun.run.sourceVersion ?? 'latest'
        },
        includeReason
      } );
      toast.success( 'Run created successfully' );
      setSShowRerunModal( false );
      navigate( `/evaluations/runs?eid=${ qEvaluationId }` );
    } catch( e ) {
      console.error( e );
      toast.error( formatRunCreateError( parseRunCreateError( e ).detail ) );
      setSIsRerunning( false );
    }
  };

  return (
    <div className={ clsx( 'page' ) }>
      <ToastContainer />
      {
        sIsLoading
          ? <Loader />
          : (
              <>
                {
                  sShowSourceModal && sSource && (
                    <ModalSourceShow
                      source={ sSource }
                      onCloseAction={ () => setSShowSourceModal( false ) }
                    />
                  )
                }
                {
                  sShowMetricModal && sSelectedMetricConfig && (
                    <ModalMetricShow
                      metricConfig={ sSelectedMetricConfig }
                      onCloseAction={ () => setSShowMetricModal( false ) }
                    />
                  )
                }
                {
                  sShowRerunModal && (
                    <ModalRunRerun
                      onClose={ () => setSShowRerunModal( false ) }
                      onConfirm={ _handleRerun }
                      sourceName={ sSourceName }
                      sourceVersion={ sRun?.run.sourceVersion }
                      isLoading={ sIsRerunning }
                    />
                  )
                }
                {
                  sShowExampleModal && sSelectedExampleId && (
                    <ModalExampleShow
                      exampleId={ sSelectedExampleId }
                      sourceId={ sRun?.run.sourceId ?? '' }
                      sourceVersion={ sRun?.run.sourceVersion ?? 'latest' }
                      metricResults={ sExampleMetricDataMap[ sSelectedExampleId ] ?? {} }
                      metricKeys={ sMetricKeys }
                      metricProviderNames={ sMetricProviderNames }
                      onCloseAction={ () => {
                        setSShowExampleModal( false );
                        setSSelectedExampleId( null );
                      } }
                    />
                  )
                }
                <div className={ clsx( 'layout__row', 'space_none', styles.layout__info ) }>
                  <EvaluationInfoHeader
                    evaluation={ sEvaluation }
                    run={ sRun }
                    isRerunning={ sIsRerunning }
                    onRerunClick={ () => setSShowRerunModal( true ) }
                  />
                </div>
                <div className={ clsx( 'layout__row', 'pad', styles.layout__description ) }>
                  <span className={ styles.info_description }>{ sEvaluation?.description }</span>
                </div>
                <div className={ clsx( 'layout__row', 'pad', styles.layout__status ) }>
                  <RunInfo
                    run={ sRun }
                    sourceName={ sSourceName }
                    evaluation={ sEvaluation }
                    metricProviderNames={ sMetricProviderNames }
                    metricProviderVersions={ sMetricProviderVersions }
                    hasLitellmError={ hasLitellmError }
                    onShowSourceModal={ () => setSShowSourceModal( true ) }
                    onShowMetricModal={ ( config ) => {
                      setSSelectedMetricConfig( config );
                      setSShowMetricModal( true );
                    } }
                  />
                </div>
                { runSchedule && (
                  <div className={ clsx( 'layout__row', 'pad', styles.schedule_card ) } data-testid="run-schedule-card">
                    <div className={ styles.schedule_card_inner }>
                      <span className={ styles.schedule_card_label }>Run Schedule</span>
                      <div className={ styles.schedule_card_body }>
                        <span className={ styles.schedule_cadence }>{ formatCronCadence( runSchedule.cron_expression ) }</span>
                        { match( runSchedule.state )
                          .with( 'ACTIVE', () => (
                            <span className={ styles.schedule_next }>
                              Next run:{ ' ' }
                              <TimeFormatter
                                sourceType={ TimeSourceTypeEnum.ISO }
                                value={ runSchedule.next_fire_at }
                                opts={ { useLocalZone } }
                                outputFormat={ OutputFormatEnum.STRING_SINGLE }
                              />
                            </span>
                          ) )
                          .with( 'PAUSED', () => (
                            <span className={ styles.schedule_next }>Schedule paused</span>
                          ) )
                          .with( 'DISABLED', () => (
                            <span className={ styles.schedule_next }>Schedule disabled</span>
                          ) )
                          .exhaustive() }
                      </div>
                    </div>
                  </div>
                ) }
                { runProgress && (
                  <div className={ clsx( 'layout__row', 'pad', styles.progress_banner ) }>
                    <div className={ styles.progress_info }>
                      <span className={ styles.progress_dot } />
                      <span>Run in progress — { runProgress.syncPercentComplete }% complete</span>
                      <span className={ styles.progress_detail }>
                        ({ runProgress.syncSucceeded + runProgress.syncFailed } / { runProgress.totalSyncTasks } tasks)
                      </span>
                    </div>
                    <div className={ styles.progress_bar_container }>
                      <div
                        className={ styles.progress_bar }
                        style={ { width: `${ runProgress.syncPercentComplete }%` } }
                      />
                    </div>
                  </div>
                ) }
                <div className={ clsx( 'layout__row', 'pad', 'space_none', styles.layout__stats ) }>
                  <Stats sMetricKeys={ sMetricKeys } sMetricDataMap={ sMetricDataMap } />
                </div>
                <div className={ clsx( 'layout__row', 'pad', styles.conversations_header ) }>
                  <Heading as="h2" size="md">Conversations</Heading>
                  { sHasAnyReasons && (
                    <Button
                      variant='secondary'
                      size='sm'
                      icon={ <Icon icon={ sAllReasonsShown ? PiCaretCircleUpBold : PiCaretCircleDownBold } aria-hidden="true" /> }
                      onClick={ sAllReasonsShown ? _handleHideAllReasons : _handleShowAllReasons }
                    >
                      { sAllReasonsShown ? 'Hide all run reasons' : 'Show all run reasons' }
                    </Button>
                  ) }
                </div>
                <div className={ styles.results_table }>
                  <NativeTable table={ resultsTable }/>
                </div>
              </>
            )
      }
    </div>
  );
}
